/**
 * Subscription Manager Component
 *
 * Provides a comprehensive UI for managing subscriptions including:
 * - Subscription list with status indicators
 * - MRR/ARR metrics display
 * - Subscription actions (cancel, pause, change plan)
 * - Filtering and search
 */

import React, { useState, useMemo } from 'react';
import {
  Subscription,
  SubscriptionStatus,
  Customer,
  PricingPlan,
  MRRSummary,
} from '../../types';

// =============================================================================
// TYPES
// =============================================================================

interface SubscriptionManagerProps {
  subscriptions: Subscription[];
  customers: Record<string, Customer>;
  plans: Record<string, PricingPlan>;
  mrrSummary: MRRSummary;
  onSubscriptionClick: (subscription: Subscription) => void;
  onChangePlan: (subscriptionId: string) => void;
  onCancel: (subscriptionId: string) => void;
  onPause: (subscriptionId: string) => void;
  onResume: (subscriptionId: string) => void;
  onCreateSubscription?: () => void;
  loading?: boolean;
}

interface SubscriptionRowProps {
  subscription: Subscription;
  customer: Customer | undefined;
  plan: PricingPlan | undefined;
  onClick: () => void;
  onAction: (action: string) => void;
}

interface MetricsCardProps {
  label: string;
  value: string | number;
  subtext?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const formatCurrency = (amount: number | null, currency = 'USD'): string => {
  if (amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusConfig = (
  status: SubscriptionStatus
): { label: string; color: string; bgColor: string } => {
  const configs: Record<
    SubscriptionStatus,
    { label: string; color: string; bgColor: string }
  > = {
    trialing: { label: 'Trial', color: 'text-blue-700', bgColor: 'bg-blue-100' },
    active: { label: 'Active', color: 'text-green-700', bgColor: 'bg-green-100' },
    past_due: {
      label: 'Past Due',
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100',
    },
    paused: { label: 'Paused', color: 'text-gray-700', bgColor: 'bg-gray-100' },
    canceled: { label: 'Canceled', color: 'text-red-700', bgColor: 'bg-red-100' },
    expired: { label: 'Expired', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  };
  return configs[status];
};

const getDaysUntilRenewal = (endDate: string): number | null => {
  if (!endDate) return null;
  const today = new Date();
  const end = new Date(endDate);
  const diffTime = end.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// =============================================================================
// METRICS CARD COMPONENT
// =============================================================================

const MetricsCard: React.FC<MetricsCardProps> = ({
  label,
  value,
  subtext,
  trend,
  trendValue,
}) => {
  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-gray-500',
  };

  const trendIcons = {
    up: '↑',
    down: '↓',
    neutral: '→',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-sm text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="mt-1 flex items-baseline">
        <span className="text-2xl font-semibold text-gray-900">{value}</span>
        {trend && trendValue && (
          <span className={`ml-2 text-sm ${trendColors[trend]}`}>
            {trendIcons[trend]} {trendValue}
          </span>
        )}
      </div>
      {subtext && <div className="mt-1 text-xs text-gray-500">{subtext}</div>}
    </div>
  );
};

// =============================================================================
// SUBSCRIPTION ROW COMPONENT
// =============================================================================

const SubscriptionRow: React.FC<SubscriptionRowProps> = ({
  subscription,
  customer,
  plan,
  onClick,
  onAction,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const statusConfig = getStatusConfig(subscription.status);
  const daysUntilRenewal = getDaysUntilRenewal(subscription.currentPeriodEnd);

  const menuItems = useMemo(() => {
    const items: { label: string; action: string; danger?: boolean }[] = [];

    if (subscription.status === 'active') {
      items.push({ label: 'Change Plan', action: 'change-plan' });
      items.push({ label: 'Pause Subscription', action: 'pause' });
      items.push({ label: 'Cancel', action: 'cancel', danger: true });
    } else if (subscription.status === 'paused') {
      items.push({ label: 'Resume', action: 'resume' });
      items.push({ label: 'Cancel', action: 'cancel', danger: true });
    } else if (
      subscription.status === 'canceled' &&
      !subscription.endedAt
    ) {
      items.push({ label: 'Reactivate', action: 'reactivate' });
    }

    return items;
  }, [subscription.status, subscription.endedAt]);

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
    >
      {/* Customer & Subscription */}
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">
          {customer?.companyName || 'Unknown Customer'}
        </div>
        <div className="text-sm text-gray-500">
          {subscription.subscriptionNumber}
        </div>
      </td>

      {/* Plan */}
      <td className="px-4 py-3">
        <div className="text-gray-900">{plan?.planName || subscription.planName}</div>
        <div className="text-sm text-gray-500">
          {subscription.billingInterval}
        </div>
      </td>

      {/* MRR */}
      <td className="px-4 py-3 text-right">
        <div className="text-gray-900 font-medium">
          {formatCurrency(subscription.mrr, subscription.currency)}
        </div>
        <div className="text-sm text-gray-500">MRR</div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color} ${statusConfig.bgColor}`}
        >
          {statusConfig.label}
        </span>
        {subscription.cancelAtPeriodEnd && (
          <div className="text-xs text-red-600 mt-1">
            Cancels at period end
          </div>
        )}
      </td>

      {/* Renewal Date */}
      <td className="px-4 py-3">
        <div className="text-gray-900">
          {formatDate(subscription.currentPeriodEnd)}
        </div>
        {daysUntilRenewal !== null && daysUntilRenewal <= 14 && (
          <div
            className={`text-xs ${
              daysUntilRenewal <= 7 ? 'text-red-600' : 'text-yellow-600'
            }`}
          >
            {daysUntilRenewal} days
          </div>
        )}
      </td>

      {/* Actions */}
      <td
        className="px-4 py-3 text-right relative"
        onClick={(e) => e.stopPropagation()}
      >
        {menuItems.length > 0 && (
          <div className="relative inline-block">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded"
            >
              ⋮
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  {menuItems.map((item) => (
                    <button
                      key={item.action}
                      onClick={() => {
                        setShowMenu(false);
                        onAction(item.action);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                        item.danger ? 'text-red-600' : 'text-gray-700'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

// =============================================================================
// FILTERS COMPONENT
// =============================================================================

interface FiltersProps {
  statusFilter: SubscriptionStatus | 'all';
  onStatusChange: (status: SubscriptionStatus | 'all') => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  planFilter: string | 'all';
  onPlanChange: (planId: string | 'all') => void;
  plans: PricingPlan[];
}

const Filters: React.FC<FiltersProps> = ({
  statusFilter,
  onStatusChange,
  searchQuery,
  onSearchChange,
  planFilter,
  onPlanChange,
  plans,
}) => {
  const statusOptions: { value: SubscriptionStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'trialing', label: 'Trial' },
    { value: 'past_due', label: 'Past Due' },
    { value: 'paused', label: 'Paused' },
    { value: 'canceled', label: 'Canceled' },
  ];

  return (
    <div className="flex items-center gap-4 p-4 border-b border-gray-200">
      {/* Status Filter */}
      <select
        value={statusFilter}
        onChange={(e) =>
          onStatusChange(e.target.value as SubscriptionStatus | 'all')
        }
        className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
      >
        {statusOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Plan Filter */}
      <select
        value={planFilter}
        onChange={(e) => onPlanChange(e.target.value)}
        className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
      >
        <option value="all">All Plans</option>
        {plans.map((plan) => (
          <option key={plan.id} value={plan.id}>
            {plan.planName}
          </option>
        ))}
      </select>

      {/* Search */}
      <div className="flex-1">
        <input
          type="text"
          placeholder="Search by customer or subscription number..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full max-w-md border border-gray-300 rounded-md px-3 py-1.5 text-sm"
        />
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({
  subscriptions,
  customers,
  plans,
  mrrSummary,
  onSubscriptionClick,
  onChangePlan,
  onCancel,
  onPause,
  onResume,
  onCreateSubscription,
  loading = false,
}) => {
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | 'all'>(
    'all'
  );
  const [planFilter, setPlanFilter] = useState<string | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter subscriptions
  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((sub) => {
      // Status filter
      if (statusFilter !== 'all' && sub.status !== statusFilter) {
        return false;
      }

      // Plan filter
      if (planFilter !== 'all' && sub.planId !== planFilter) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const customer = customers[sub.customerId];
        const searchLower = searchQuery.toLowerCase();
        const matchesCustomer =
          customer?.companyName.toLowerCase().includes(searchLower);
        const matchesNumber = sub.subscriptionNumber
          .toLowerCase()
          .includes(searchLower);
        if (!matchesCustomer && !matchesNumber) {
          return false;
        }
      }

      return true;
    });
  }, [subscriptions, statusFilter, planFilter, searchQuery, customers]);

  // Get unique plans for filter
  const uniquePlans = useMemo(() => {
    const planIds = new Set(subscriptions.map((s) => s.planId));
    return Object.values(plans).filter((p) => planIds.has(p.id));
  }, [subscriptions, plans]);

  // Handle action
  const handleAction = (subscriptionId: string, action: string) => {
    switch (action) {
      case 'change-plan':
        onChangePlan(subscriptionId);
        break;
      case 'cancel':
        onCancel(subscriptionId);
        break;
      case 'pause':
        onPause(subscriptionId);
        break;
      case 'resume':
        onResume(subscriptionId);
        break;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading subscriptions...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header with Metrics */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-gray-900">Subscriptions</h1>
            {onCreateSubscription && (
              <button
                onClick={onCreateSubscription}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                + New Subscription
              </button>
            )}
          </div>

          {/* Unified Pricing Notice */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>Tip:</strong> Use <em>Plan Tiers</em> tab to configure pricing models (flat fee, per-seat, tiered, revenue share, hybrid).
            Subscriptions created from Plan Tiers automatically inherit pricing, capabilities, and credit allocations.
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-4 gap-4">
            <MetricsCard
              label="Total MRR"
              value={formatCurrency(mrrSummary.totalMrr)}
              subtext={`ARR: ${formatCurrency(mrrSummary.totalArr)}`}
            />
            <MetricsCard
              label="Active Subscriptions"
              value={mrrSummary.totalSubscriptions}
              subtext={`${mrrSummary.totalCustomers} customers`}
            />
            <MetricsCard
              label="Net MRR Change"
              value={formatCurrency(mrrSummary.netMrrChange)}
              trend={
                mrrSummary.netMrrChange > 0
                  ? 'up'
                  : mrrSummary.netMrrChange < 0
                  ? 'down'
                  : 'neutral'
              }
              trendValue="this month"
            />
            <MetricsCard
              label="Churn"
              value={formatCurrency(mrrSummary.churnMrr)}
              subtext={`Expansion: ${formatCurrency(mrrSummary.expansionMrr)}`}
            />
          </div>
        </div>

        {/* Filters */}
        <Filters
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          planFilter={planFilter}
          onPlanChange={setPlanFilter}
          plans={uniquePlans}
        />
      </div>

      {/* Subscriptions Table */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  MRR
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Renews
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No subscriptions found
                  </td>
                </tr>
              ) : (
                filteredSubscriptions.map((subscription) => (
                  <SubscriptionRow
                    key={subscription.id}
                    subscription={subscription}
                    customer={customers[subscription.customerId]}
                    plan={plans[subscription.planId]}
                    onClick={() => onSubscriptionClick(subscription)}
                    onAction={(action) => handleAction(subscription.id, action)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;
